/* global globalThis */
import { useEffect } from "react";
import axiosClient from "../../api-client/AxiosClient";
const application_id = process.env.REACT_APP_APPLICATION_ID;

export const getUserSelectedOrgConfig = async () => {
    try {
        const response = await axiosClient.get(
            "/api/Organization/GetUserSelectedOrgConfig"
        );
        return response?.data ?? null;
    } catch (error) {
        console.error("Failed to get user selected org config:", error);
        return null;
    }
};

export const setUserSelectedOrgConfig = async () => {
    try {
        const organizationConfigId = localStorage.getItem("organization_config_id");
        const selectedCorporation = localStorage.getItem("selectedCorporation");
        const UserMappingFileId = localStorage.getItem("UserMappingFileId");
        const organizationId = selectedCorporation
            ? JSON.parse(selectedCorporation)?.value?.toString()
            : null;

        if (!organizationConfigId || !organizationId) {
            return null;
        }

        const payload = {
            organizationConfigId: String(organizationConfigId),
            organizationId: String(organizationId),
            userMappingFileId: String(UserMappingFileId),
        };

        const response = await axiosClient.post(
            "/api/Organization/SetUserSelectedOrgConfig",
            payload
        );

        return response?.data ?? null;
    } catch (error) {
        console.error("Failed to set user selected org config:", error);
        return null;
    }
};



// Helper functions for merging roles and permissions
const MODULE_MAPPING = {
    CCDashboard: "Dashboard",
    CCDataSource: "DataSource",
};

const mergeModulePermissions = (merged, module, permissions) => {
    if (!merged[module]) {
        merged[module] = {};
    }
    if (permissions && typeof permissions === "object") {
        Object.keys(permissions).forEach((key) => {
            // Using OR logic: if any role has the permission, the user has it
            merged[module][key] = !!(merged[module][key] || permissions[key]);
        });
    }
};

const processRole = (merged, role) => {
    if (role && typeof role === "object") {
        Object.entries(role).forEach(([module, permissions]) => {
            // Ignore legacy keys to ensure the CC-prefixed versions are the definitive source
            if (module === "Dashboard" || module === "DataSource") {
                return;
            }
            const targetModule = MODULE_MAPPING[module] || module;
            mergeModulePermissions(merged, targetModule, permissions);
        });
    }
};

const mergeRolesAndPermissions = (rolesData) => {
    const merged = {};
    if (rolesData && typeof rolesData === "object") {
        Object.values(rolesData).forEach((role) => {
            processRole(merged, role);
        });
    }
    return merged;
};

export const fetchPermissions = async (configId) => {
    try {
        if (!configId) {
            globalThis.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: null }));
            return null;
        }

        const response = await axiosClient.get(`/api/User/Permissions?OrgConfigId=${configId}&ApplicationId=${application_id}`);

        const permissions = response?.data ? mergeRolesAndPermissions(response.data) : null;
        if (permissions) {
            sessionStorage.setItem("rolesAndPermission", JSON.stringify(permissions));
        }

        // Dispatch event to notify listeners (like layout.js) that permissions are loaded/ready
        globalThis.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: permissions }));
        return permissions;
    } catch (error) {
        console.error("Failed to fetch permissions:", error);
        // Dispatch null to prevent infinite loading state
        globalThis.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: null }));
        return null;
    }
};

export const useOrgConfigSync = () => {
    useEffect(() => {
        // Initial check on mount to ensure layout.js unblocks
        const storedRoles = sessionStorage.getItem("rolesAndPermission");
        const configId = localStorage.getItem("organization_config_id");

        if (storedRoles) {
            // Already have everything, just ensure Layout is aware if it missed the initial state
            globalThis.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: JSON.parse(storedRoles) }));
        } else if (configId) {
            // We have a config but no roles - fetch them
            fetchPermissions(configId);
        } else {
            // NO config and NO roles - unblock UI so user can select from Header
            globalThis.dispatchEvent(new CustomEvent("permissionsUpdated", { detail: null }));
        }

        const handleStorageChange = async (e) => {
            if (
                e.detail?.key === "organization_config_id" ||
                e.detail?.key === "selectedCorporation"
            ) {
                await setUserSelectedOrgConfig();

                // Fetch permissions if config changed
                if (e.detail?.key === "organization_config_id" && e.detail?.newValue) {
                    await fetchPermissions(e.detail.newValue);
                }
            }
        };

        globalThis.addEventListener("customStorageChange", handleStorageChange);
        return () => {
            globalThis.removeEventListener("customStorageChange", handleStorageChange);
        };
    }, []);
};